import { ReactNode } from "react";
import { useTheme } from "@mui/material";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";

export type PageHeaderProps = {
  /** small overline, usually the nav section (e.g. "Content") */
  overline?: string;
  title: string;
  subtitle?: string;
  /** right-aligned actions (primary button, etc.) */
  actions?: ReactNode;
  /** rendered below the title row — usually a DSSegmentedTabs */
  tabs?: ReactNode;
};

/**
 * Standard page chrome: overline + big title + subtitle on the left,
 * actions on the right, optional tab strip underneath.
 * Stacks vertically on small screens.
 */
const PageHeader = ({
  overline,
  title,
  subtitle,
  actions,
  tabs,
}: PageHeaderProps) => {
  const { palette } = useTheme();

  return (
    <DSBox sx={{ mb: 3 }}>
      <DSBox
        sx={{
          display: "flex",
          flexDirection: { xxs: "column", sm: "row" },
          alignItems: { xxs: "stretch", sm: "flex-end" },
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <DSBox sx={{ minWidth: 0 }}>
          {overline && (
            <DSTypography
              variant="subtitle2"
              sx={{
                color: palette.surface.accentText,
                textTransform: "uppercase",
                letterSpacing: "0.14em",
                fontSize: "11px",
                mb: 0.5,
              }}
            >
              {overline}
            </DSTypography>
          )}
          <DSTypography
            variant="h4"
            sx={{
              fontSize: { xxs: "26px", md: "34px" },
              lineHeight: 1.2,
              color: palette.text.primary,
            }}
          >
            {title}
          </DSTypography>
          {subtitle && (
            <DSTypography
              variant="body2"
              sx={{
                color: palette.text.secondary,
                mt: 0.75,
                maxWidth: 640,
                display: { xxs: "none", sm: "block" },
              }}
            >
              {subtitle}
            </DSTypography>
          )}
        </DSBox>
        {actions && (
          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            {actions}
          </DSBox>
        )}
      </DSBox>
      {tabs && <DSBox sx={{ mt: 2.5 }}>{tabs}</DSBox>}
    </DSBox>
  );
};

export default PageHeader;
